import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { useDocumentContentUrl } from "./api/useDocumentContentUrl";
import { getOpenerLockKey } from "./coord/openerLock";
import { getTabId } from "./coord/tabId";
import { useDocumentSessionState } from "./coord/useDocumentSessionState";
import DocumentViewerPage from "./DocumentViewerPage";
import { documentPayloadStorageKey } from "./openViewerWindow";
import { useContentBlobUrl } from "./useContentBlobUrl";
import type { ViewerDocument } from "./viewer.types";

/** Every document this viewer opens is a PDF (AppXtender documents only). */
const PDF_CONTENT_TYPE = "application/pdf";

/** Display metadata handed off from the opener via `sessionStorage`. */
interface StoredDocumentPayload {
  fileName?: string;
  reviewedBy?: string | null;
}

/**
 * Reads this document's display metadata handoff. The opener writes it to
 * `sessionStorage` immediately before `window.open()`, and the browser clones
 * the opener's `sessionStorage` into this new same-origin window at creation
 * time — so it's already present on this window's very first render, no
 * BroadcastChannel round trip required.
 * @param {string} documentId - The document id from this window's URL.
 * @returns {StoredDocumentPayload} The stored metadata, or `{}` if absent/unreadable.
 */
function readStoredPayload(documentId: string): StoredDocumentPayload {
  if (!documentId) {
    return {};
  }
  try {
    const raw = sessionStorage.getItem(documentPayloadStorageKey(documentId));
    return raw ? (JSON.parse(raw) as StoredDocumentPayload) : {};
  } catch {
    return {};
  }
}

// Container for the dedicated per-document viewer window (OR-22558: one window
// per document). Unlike the earlier shared-viewer design, this window never
// needs to learn *which* document to show over BroadcastChannel — its
// appId/documentId come straight from its own URL, and its display metadata
// from the sessionStorage handoff above. It still listens for the opener's
// live session state (auth + VLA lock), since that gates whether it's allowed
// to keep showing content.
function DocumentViewer() {
  const [searchParameters] = useSearchParams();
  const appId = searchParameters.get("appId") ?? "";
  const documentId = searchParameters.get("docId") ?? "";

  const storedPayload = useMemo(() => readStoredPayload(documentId), [documentId]);
  const lockKey = useMemo(
    () => (appId && documentId ? getOpenerLockKey(appId, documentId) : undefined),
    [appId, documentId]
  );

  // Claim this document's opener lock under this window's own tabId (never
  // the opener's) so the unload cleanup below can actually match it.
  useEffect(() => {
    if (!lockKey) {
      return;
    }
    localStorage.setItem(lockKey, JSON.stringify({ tabId: getTabId(), timestamp: Date.now() }));
  }, [lockKey]);

  useEffect(() => {
    if (!lockKey) {
      return;
    }
    const handleBeforeUnload = () => {
      const lockData = localStorage.getItem(lockKey);
      if (lockData) {
        try {
          const { tabId } = JSON.parse(lockData);
          if (tabId === getTabId()) {
            localStorage.removeItem(lockKey);
          }
        } catch {
          // Malformed lock data, clear it
          localStorage.removeItem(lockKey);
        }
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [lockKey]);

  const { sessionState, heldByMe } = useDocumentSessionState(appId);
  const isActive = sessionState === "active";

  const {
    data: contentUrl,
    isLoading: isUrlLoading,
    isFetching: isUrlFetching,
    isError: isUrlError,
  } = useDocumentContentUrl(appId, documentId, isActive);
  const {
    blobUrl,
    isLoading: isBlobLoading,
    isError: isBlobError,
  } = useContentBlobUrl(contentUrl, isActive && Boolean(contentUrl));

  const isLoading = isUrlLoading || isUrlFetching || isBlobLoading;
  const isError = isUrlError || isBlobError;

  const hasDocument = isActive && Boolean(blobUrl);
  const document: ViewerDocument | undefined =
    hasDocument && blobUrl
      ? {
          fileName: storedPayload.fileName ?? documentId,
          contentType: PDF_CONTENT_TYPE,
          src: blobUrl,
          reviewedBy: storedPayload.reviewedBy,
        }
      : undefined;

  useEffect(() => {
    if (hasDocument) {
      globalThis.document.title = `${storedPayload.fileName ?? documentId} - Approval#: ${appId}`;
    }
  }, [hasDocument, storedPayload.fileName, documentId, appId]);

  return (
    <DocumentViewerPage
      document={document}
      appId={appId}
      docId={documentId}
      // A background refetch of the content URL (window focus/reconnect)
      // after a prior failure leaves `isUrlError` true and `isUrlLoading`
      // false for its whole duration (TanStack Query only clears `isError`
      // once the retry settles) — fold `isUrlFetching` into `isLoading` so a
      // stale error isn't shown while a retry is in flight.
      error={isActive && isError && !isLoading}
      isLoading={isActive && isLoading}
      isHeld={heldByMe}
    />
  );
}

export default DocumentViewer;
